export type TelegramInvoiceStatus = 'paid' | 'cancelled' | 'failed' | 'pending';

export type TelegramTransactionStatus = 'Pending' | 'Cancelled' | 'Paid' | 'PaidCancelled';

export type TelegramStarsPaymentErrorCode = 'invoice_unavailable' | 'cancelled' | 'failed' | 'confirmation_timeout';

export class TelegramStarsPaymentError extends Error {
  constructor(public readonly code: TelegramStarsPaymentErrorCode) {
    super(`Telegram Stars payment error: ${code}`);
    this.name = 'TelegramStarsPaymentError';
  }
}

export const isTelegramStarsPaymentError = (
  error: unknown,
  code?: TelegramStarsPaymentErrorCode,
): error is TelegramStarsPaymentError =>
  error instanceof TelegramStarsPaymentError && (code === undefined || error.code === code);

type OpenInvoice = (url: string, callback: (status: TelegramInvoiceStatus) => void) => void;

type ConfirmTelegramStarsPaymentParams = {
  transactionId: string;
  invoiceUrl: string;
  openInvoice?: OpenInvoice;
  checkStatus: (transactionId: string) => Promise<TelegramTransactionStatus>;
  pollIntervalMs?: number;
  maxPollAttempts?: number;
  sleep?: (timeoutMs: number) => Promise<void>;
};

const defaultSleep = (timeoutMs: number) => new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));

export async function confirmTelegramStarsPayment({
  transactionId,
  invoiceUrl,
  openInvoice,
  checkStatus,
  pollIntervalMs = 1500,
  maxPollAttempts = 80,
  sleep = defaultSleep,
}: ConfirmTelegramStarsPaymentParams): Promise<void> {
  if (!openInvoice) {
    throw new TelegramStarsPaymentError('invoice_unavailable');
  }

  const invoiceStatus = await new Promise<TelegramInvoiceStatus>((resolve, reject) => {
    try {
      openInvoice(invoiceUrl, resolve);
    } catch {
      reject(new TelegramStarsPaymentError('invoice_unavailable'));
    }
  });

  if (invoiceStatus === 'cancelled') {
    throw new TelegramStarsPaymentError('cancelled');
  }
  if (invoiceStatus === 'failed') {
    throw new TelegramStarsPaymentError('failed');
  }

  for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
    try {
      const status = await checkStatus(transactionId);

      if (status === 'Paid') {
        return;
      }
      if (status === 'Cancelled' || status === 'PaidCancelled') {
        throw new TelegramStarsPaymentError('cancelled');
      }
    } catch (error) {
      if (isTelegramStarsPaymentError(error)) {
        throw error;
      }
      // A temporary status request failure must not turn a successful Telegram payment into a failed checkout.
    }

    if (attempt < maxPollAttempts - 1) {
      await sleep(pollIntervalMs);
    }
  }

  throw new TelegramStarsPaymentError('confirmation_timeout');
}
